import React, { PureComponent } from 'react';
import type { WrappedComponentProps, IntlShape, MessageDescriptor } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import isEqual from 'lodash/isEqual';

import withAnalyticsEvents from '@atlaskit/analytics-next/withAnalyticsEvents';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import AvatarGroup from '@atlaskit/avatar-group';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';

import {
	openInlineCommentInEditor,
	scrollCommentIntoView,
	scrollPageCommentIntoView,
} from '@confluence/comments-util';
import {
	QUICK_RELOAD_EXPERIENCE,
	ExperienceTimeout,
	getExperienceTracker, // eslint-disable-line no-restricted-imports
	stopExperienceOnError,
} from '@confluence/experience-tracker';
import type { FlagsStateContainer } from '@confluence/flags';
import { LIVE_PAGE_SUBTYPE } from '@confluence/live-pages-utils';
import type { RoutesContextType } from '@confluence/route-manager';
import { withRoutesContext } from '@confluence/route-manager';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter';

import type { SmartTimer } from './SmartTimer';
import type { FlagSpec, StreamEvent, FlagType } from './QuickReloadStreams';
import { CommentsStream, EditsStream } from './QuickReloadStreams';
import { i18n } from './quickReloadi18n';
import { PageMode, PubSubState, PubSubFlagType } from './pubSubConstants';
import type { MissedNotificationResult, PubSubData, CommentEvent } from './pubSubDataTypes';
import { shouldAddPageCommentEvents, type UserInfo } from './pubSubUtils';

declare global {
	interface Window {
		// Quick reload is a background component that might be delayed in mounting
		__quickReloadMountedForIntegrationTest?: boolean;
	}
}

/**
 * The <QuickReloadFlagsI18nProvider> is a component that provides a
 * <QuickReload> with:
 *
 * 1) an i18n service
 * 2) an abstraction of the 'flags' component to manage the ordering and
 *    disposal of flags, two different flags being available for edits
 *    vs. comments
 * 3) a manager to stop and start the polling service based on the component
 *    mounting, unmounting, or having its contentId prop change
 */

type Annotations = {
	id: string;
	type: string;
};

type QuickReloadFlagsI18nProviderComponentProps = WithAnalyticsEventsProps &
	WrappedComponentProps & {
		flagsStateContainer: FlagsStateContainer;
		timer: any; // expedient;
		contentId: string;
		contentType: string;
		currentAccountId: string | null;
		lastPollTime: number;
		reloadContent?: () => Promise<void>;
		reloadComments?: (url?: string) => Promise<void>;
		setLastNotificationReceivedTime: (time: number) => void;
		documentVisible?: boolean;
		setUserReadyCallback?: (arg1, arg2) => void;
		userFocusedCallback: () => void;
		canUsePubSub?: boolean;
		pubSubData?: PubSubData;
		pubSubState?: PubSubState;
		fetchNotifications?: (
			since,
			contentId,
			pageMode,
		) => Promise<MissedNotificationResult | undefined>;
		poller: (
			intl: IntlShape,
			Timer: SmartTimer,
			contentId: string,
			contentType: string,
			currentAccountId: string | null,
			lastPollTime: number,
			makeShowFlag: (any) => any, // expedient
			hideFlags: () => void, // expedient
			setLastNotificationReceivedTime: (time: number) => void,
			pageMode: PageMode,
			fetchOnStart?: boolean,
		) => () => void;
		pageMode: PageMode;
		isPageCommentsAutoReloadEnabled?: boolean;
		documentNeedsUpdate?: boolean;
		setDocumentNeedsUpdate?: (documentNeedsUpdate: boolean) => void;
		pageCommentsSectionInView?: boolean;
		updateUnreadCommentsOnFirstRender?: (shouldUpdateUnreadCommentsOnFirstRender: boolean) => void;
		isUnreadCommentsIsEnabled?: boolean;
		selectedAnnotations?: Annotations[];
	};

type QuickReloadAnalyticsAttributes = {
	veryQuickReload?: boolean;
	contentId?: string;
	type?: 'edit' | 'comment';
	realtimeDataSource?: 'pubsubs' | 'subscriptions' | 'polling';
	pageMode?: 'edit' | 'view' | typeof LIVE_PAGE_SUBTYPE;
	commentType?: string;
};

type QuickReloadState = {
	commentStream?: CommentsStream;
	editsStream?: EditsStream;
	mostRecentPageCommentFlagID?: number | null;
};

const startReloadExperience = (attributes: QuickReloadAnalyticsAttributes) => {
	const experienceTracker = getExperienceTracker();
	experienceTracker.start({
		name: QUICK_RELOAD_EXPERIENCE,
		timeout: ExperienceTimeout.DEFAULT,
		attributes,
	});
};

const failReloadExperience = (error: any) => {
	stopExperienceOnError(QUICK_RELOAD_EXPERIENCE, error);
};

const succeedReloadExperience = () => {
	getExperienceTracker().succeed({ name: QUICK_RELOAD_EXPERIENCE });
};

let isPollerActive = false;

/**
 * A property to store the timeout for fetching missed notifications when a user leaves a tab and comes back. Only for pubsubs.
 * If a user cycles through their tabs quickly, we will invoke the api call to fetch missed notifications as soon as they re-open the tab.
 * A timeout was added so we save API calls and only fetch missed notifications once they stay on the tab for more than 4 seconds.
 */
let pubsubsMissedNotificationsTimeout: NodeJS.Timeout | number | null = null;

export class QuickReloadFlagsI18nProviderComponent extends PureComponent<
	QuickReloadFlagsI18nProviderComponentProps & RoutesContextType,
	QuickReloadState
> {
	state: QuickReloadState = {};

	componentDidMount() {
		const commentStream = new CommentsStream(
			this.props.intl,
			this.props.currentAccountId,
			this.props.contentType,
			this.makeShowFlag('comment'),
			this.getDocumentNeedsUpdate,
		);
		const editsStream = new EditsStream(
			this.props.intl,
			this.props.currentAccountId,
			this.props.contentType,
			this.makeShowFlag('edit'),
		);

		if (!this.props.canUsePubSub) {
			this.startPoller(this.props.lastPollTime);
		} else {
			this.setState((state) => {
				return {
					...state,
					commentStream,
					editsStream,
					mostRecentPageCommentFlagID: null,
				};
			});
		}

		this._isMounted = true;
		window.__quickReloadMountedForIntegrationTest = true;
	}

	componentDidUpdate(prevProps) {
		const {
			documentVisible = true,
			contentId,
			contentType,
			lastPollTime,
			setLastNotificationReceivedTime,
			canUsePubSub,
			pubSubData,
			pubSubState,
			fetchNotifications,
			pageCommentsSectionInView,
		} = this.props;
		const { commentStream, editsStream } = this.state;

		const contentIdOrTypeChanged =
			contentId !== prevProps.contentId || contentType !== prevProps.contentType;

		if (pageCommentsSectionInView && this.state.mostRecentPageCommentFlagID) {
			const flags = this.props.flagsStateContainer.state.flags;
			if (flags.length && flags[flags.length - 1].id === this.state.mostRecentPageCommentFlagID) {
				void this.props.flagsStateContainer.hideFlag(this.state.mostRecentPageCommentFlagID);
			}
		}

		if (contentIdOrTypeChanged) {
			this.hideFlags();
			this.resetStreamEvents();
		}

		if (!canUsePubSub) {
			// If we've lost an ability to use pubSub or switched to another page switch to polling
			if (prevProps.canUsePubSub || contentIdOrTypeChanged) {
				this.stopPoller();
				this.startPoller(lastPollTime);
			}

			return;
		}

		// Here and below we have canUsePubSub==true
		// We went back to background - no need to poll data
		if (!documentVisible && prevProps.documentVisible) {
			this.clearPubSubTimeout();
			this.stopPoller(false); // do not hide flags
			return;
		}

		this.getMissedPubSubNotifications(
			pubSubState,
			prevProps.pubSubState,
			lastPollTime,
			fetchNotifications,
		);

		if (commentStream && editsStream) {
			if (pubSubData && !isEqual(pubSubData, prevProps.pubSubData)) {
				const lastPubSubEventReceivedTime = Date.now();
				setLastNotificationReceivedTime(
					Math.max(this.props.lastPollTime, lastPubSubEventReceivedTime),
				);

				const streamEvent: StreamEvent = {
					...pubSubData,
					displayName: pubSubData.displayName ?? '',
					accountId: pubSubData.accountId ?? '',
					profilePicturePath: pubSubData.profilePicturePath ?? '',
				};
				if (pubSubData?.flagType === PubSubFlagType.COMMENT) {
					streamEvent.markerRef = pubSubData.markerRef ?? '';
					streamEvent.adfBodyContent = pubSubData.adfBodyContent;
					streamEvent.commentType = pubSubData.commentType;
					if (
						pubSubData.isInline ||
						shouldAddPageCommentEvents(
							Boolean(this.props.isPageCommentsAutoReloadEnabled),
							!pubSubData.isInline,
							!this.props.pageCommentsSectionInView,
						)
					) {
						commentStream.addEvents([streamEvent]);
					}
				}
				if (pubSubData?.flagType === PubSubFlagType.EDIT) {
					editsStream.addEvents([streamEvent]);
				}
				return;
			}
		}
	}

	componentWillUnmount() {
		this.resetStreamEvents();
		this.hideFlags();
		this.stopPoller();
		this._isMounted = false;
		this.clearPubSubTimeout();
		window.__quickReloadMountedForIntegrationTest = false;
	}

	private _isMounted = false;

	public resetStreamEvents() {
		const { commentStream, editsStream } = this.state;
		if (commentStream) {
			commentStream.resetEvents();
		}
		if (editsStream) {
			editsStream.resetEvents();
		}
	}

	public startPoller(lastPollTime: number, fetchOnStart?: boolean) {
		if (this.props.canUsePubSub) return;

		// this.props.poller is startQuickReloadPoller (except in testing)
		if (isPollerActive) {
			return;
		}
		isPollerActive = true;
		this.stopPollerCallback = this.props.poller(
			this.props.intl,
			this.props.timer,
			this.props.contentId,
			this.props.contentType,
			this.props.currentAccountId,
			lastPollTime,
			this.makeShowFlag,
			this.hideFlags,
			this.props.setLastNotificationReceivedTime,
			this.props.pageMode,
			fetchOnStart,
		);
	}

	fireReloadAnalytics = (attributes?: QuickReloadAnalyticsAttributes) => {
		const { createAnalyticsEvent } = this.props;

		if (createAnalyticsEvent)
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'quickReload',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'quickReloadButton',
					attributes: {
						veryQuickReload: false,
						...(attributes?.type === 'comment' && {
							commentType: attributes.commentType,
						}),
						...attributes,
					},
				},
			}).fire();
	};

	/*
    When a pubsub was terminated and not re-activated, there could be a time period where the user missed notifications.
    This logic will retrieve any missing notifications.
    Using the timestamp they last received a notification, we will fetch all notifications since that time and display them.
  */
	getMissedPubSubNotifications = (
		pubSubState: PubSubState | undefined,
		prevPubSubState: PubSubState | undefined,
		lastPollTime: number,
		fetchNotificationsFn,
	) => {
		if (pubSubState === PubSubState.ACTIVE && prevPubSubState === PubSubState.TERMINATED) {
			this.clearPubSubTimeout();

			// Only fetch the missed notifications if the user has stayed on the tab for 3 seconds after coming back to the tab
			pubsubsMissedNotificationsTimeout = setTimeout(() => {
				void fetchNotificationsFn(this.props.contentId, lastPollTime, this.props.pageMode).then(
					(res) => {
						// this check of content types is needed in case the promise resolves after a user has already changed pages
						if (this.props.contentId !== res.contentId) return;
						if (res.commentEvents.length) {
							// Filter out page comments if the auto-reload feature is enabled
							const filteredEvents = res.commentEvents.filter(
								(commentEvent) =>
									(commentEvent as CommentEvent).type === 'INLINE' ||
									shouldAddPageCommentEvents(
										Boolean(this.props.isPageCommentsAutoReloadEnabled),
										(commentEvent as CommentEvent).type !== 'INLINE',
										!this.props.pageCommentsSectionInView,
									),
							);

							if (filteredEvents.length) {
								this.state.commentStream?.addEvents(filteredEvents);
							}
						}

						if (res.editEvents.length) {
							this.state.editsStream?.addEvents(res.editEvents);
						}
						this.props.setLastNotificationReceivedTime(res.time);
					},
				);
			}, 5000);
		}
	};

	clearPubSubTimeout() {
		if (pubsubsMissedNotificationsTimeout) {
			clearTimeout(pubsubsMissedNotificationsTimeout);
			pubsubsMissedNotificationsTimeout = null;
		}
	}

	getDocumentNeedsUpdate = () => Boolean(this.props.documentNeedsUpdate);

	resetEvents = (isComment: boolean) => {
		const eventStream = isComment ? this.state.commentStream : this.state.editsStream;

		eventStream?.resetEvents();
	};

	reload = async (
		isComment: boolean,
		reloader: (url?: string) => Promise<void>,
		fallback: () => void,
		commentType: string,
		url?: string,
	) => {
		const { intl, push, contentId, contentType, flagsStateContainer } = this.props;

		let flagId: number | undefined;
		let message: MessageDescriptor;
		const isBlog = contentType === 'blogpost';

		if (isComment) {
			flagId = this.showingFlagIdsForType.comment;
			message = isBlog ? i18n.newCommentsLoadingBlog : i18n.newCommentsLoading;
		} else {
			flagId = this.showingFlagIdsForType.edit;
			message = isBlog ? i18n.newContentLoadingBlog : i18n.newContentLoading;
		}

		// Update flag status and content to indicate progress
		await flagsStateContainer.updateFlag(flagId, {
			description: intl.formatMessage(message),
			actions: [],
		});

		// Reload data and track the time and success/failure of the operation
		const reloadEventAttributes: QuickReloadAnalyticsAttributes = {
			veryQuickReload: true,
			contentId,
			type: isComment ? 'comment' : 'edit',
			realtimeDataSource: this.getAnalyticsRealtimeDataSource(),
			...(isComment && {
				commentType,
			}),
			pageMode: 'view',
		};

		startReloadExperience(reloadEventAttributes);

		let reloadError;
		try {
			await reloader(url);
		} catch (error) {
			reloadError = error;
		}

		this.fireReloadAnalytics(reloadEventAttributes);
		if (reloadError) {
			failReloadExperience(reloadError);
			fallback();
		} else {
			succeedReloadExperience();
		}

		void flagsStateContainer.hideFlag(flagId);
		this.resetEvents(isComment);

		// If url is provided, use it to highlight the newly added comment or,
		// in the future, content - without full page reload.
		if (url) {
			push(url, false);
		}
	};

	commentFlagRendererOnClick = async (
		flagProps: FlagSpec,
		type: FlagType,
		onClose: () => void,
		url?: string,
	) => {
		const {
			push,
			getQueryParams,
			setQueryParams,
			reloadComments,
			isPageCommentsAutoReloadEnabled,
			flagsStateContainer,
			setDocumentNeedsUpdate,
			updateUnreadCommentsOnFirstRender,
			isUnreadCommentsIsEnabled,
		} = this.props;
		const documentNeedsUpdate = this.getDocumentNeedsUpdate();
		const { commentType, markerRef, commentIsAReply, commentId } = flagProps;

		const fullReload = () => {
			if (url) {
				push(url, true);
			} else {
				window.location.reload();
			}
		};

		// We don't need a full reload as the new comment will already exist in the document
		// UNLESS the user has navigated away, we just need to set it
		// to be the focusedCommentId in the earlier case, and trigger a reload in the latter
		if (commentType !== 'page' && !documentNeedsUpdate) {
			this.flagCleanup(type, onClose);

			// Clear the query params so push works correctly
			setQueryParams({
				...getQueryParams(),
				focusedCommentId: undefined,
			});

			if (markerRef) {
				const commentElement = document.getElementById(markerRef);
				// Otherwise emit the event to open the thread
				getRendererAnnotationEventEmitter().emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
					annotationIds: [markerRef],
					eventTarget: commentElement,
				});
				if (!commentIsAReply) {
					scrollCommentIntoView(commentElement, true);
				}
			}
			// this URL contains the focusedCommentId which we'll use to scroll to the comment
			if (url) {
				push(url);
			}

			const flagId = this.showingFlagIdsForType.comment;
			if (flagId) {
				void flagsStateContainer.hideFlag(flagId);
			} else {
				void flagsStateContainer.hideAllFlags();
			}

			return;
		}

		// scroll to page comment for realtime page comments
		if (isPageCommentsAutoReloadEnabled && commentType === 'page' && commentId) {
			const commentElement = document.getElementById('comment-'.concat(commentId));
			scrollPageCommentIntoView(commentElement, true, true);
			const flagId = this.showingFlagIdsForType.comment;
			if (flagId) {
				void flagsStateContainer.hideFlag(flagId);
			} else {
				void flagsStateContainer.hideAllFlags();
			}
			return;
		}

		if (reloadComments) {
			await this.reload(true, reloadComments, fullReload, commentType, url);

			// If we got here, hide the content update flag
			if (commentIsAReply) {
				if (url) {
					push(url, false);
				}
			} else if (markerRef) {
				// Emit the event to open the thread if it's not a reply
				const commentElement = document.getElementById(markerRef);
				getRendererAnnotationEventEmitter().emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
					annotationIds: [markerRef],
					eventTarget: commentElement,
				});

				scrollCommentIntoView(commentElement, true);

				const contentRefreshFlagId = this.showingFlagIdsForType.edit;
				void flagsStateContainer.hideFlag(contentRefreshFlagId);

				if (documentNeedsUpdate) {
					setDocumentNeedsUpdate && setDocumentNeedsUpdate(false);
				}

				isUnreadCommentsIsEnabled &&
					updateUnreadCommentsOnFirstRender &&
					updateUnreadCommentsOnFirstRender(true);
			}

			const flagId = this.showingFlagIdsForType.comment;
			if (flagId) {
				void flagsStateContainer.hideFlag(flagId);
			} else {
				void flagsStateContainer.hideAllFlags();
			}

			return;
		}

		this.fireReloadAnalytics({
			pageMode: 'view',
			commentType,
		});
		fullReload();
	};

	commentFlagEditorLivePagesOnClick = async (
		markerRef: string,
		type: FlagType,
		onClose: () => void,
		commentType: string,
		push: (url: string, reload: boolean) => void,
		commentIsAReply: boolean,
		url?: string,
	) => {
		const commentElement = document.getElementById(markerRef);
		openInlineCommentInEditor(commentElement as HTMLElement, markerRef, commentIsAReply);
		await this.props.flagsStateContainer.hideFlag(this.showingFlagIdsForType.comment);

		if (url) {
			push(url, false);
		}

		this.flagCleanup(type, onClose);
		this.fireReloadAnalytics({
			pageMode: this.props.pageMode === PageMode.LIVE ? LIVE_PAGE_SUBTYPE : 'edit',
			commentType,
		});
	};

	contentFlagOnClick = async () => {
		const { reloadContent, setDocumentNeedsUpdate } = this.props;
		const fullReload = () => {
			window.location.reload();
		};

		if (reloadContent) {
			await this.reload(false, reloadContent, fullReload, '');
			setDocumentNeedsUpdate && setDocumentNeedsUpdate(false);
			return;
		}

		this.fireReloadAnalytics({
			pageMode: 'view',
		});
		fullReload();
	};

	private stopPollerCallback: (shouldHideFlags?: boolean) => void = () => {};

	public stopPoller(shouldHideFlags?: boolean) {
		isPollerActive = false;
		this.stopPollerCallback(shouldHideFlags);
	}

	private actions = [
		{
			content: (
				<div data-test-id="quick-reload-reload-button">
					{this.props.intl.formatMessage(i18n.reload)}
				</div>
			),
			onClick: this.contentFlagOnClick,
		},
	];

	showingFlagIdsForType = {
		edit: undefined,
		comment: undefined,
	};

	flagCleanup = (type: FlagType, onClose: () => void) => {
		delete this.showingFlagIdsForType[type];
		onClose();
	};

	makeShowFlag = (type) => async (flagProps: FlagSpec) => {
		const func = async () => {
			if (!this._isMounted) {
				return;
			}

			const { createAnalyticsEvent, selectedAnnotations } = this.props;
			/**
			 * Show a new flag, and, if there was already one open for this type, hide
			 * it. Doing these in the reverse order would make more sense, but if we do
			 * that, the display screws up. KTLO: look into why; I suspect flags
			 * package or FlagGroup.
			 */

			const retiringFlagId = this.showingFlagIdsForType[type];

			const { url, markerRef, onClose, commentIsAReply, uniqueUsers } = flagProps;

			/**
			 * We add a url property to new comment events.
			 * NOTE: the event type is the same for new comments or
			 * editing an existing comment (inline or page)
			 */
			const commentActions = [
				{
					content: (
						<div data-test-id="quick-reload-reload-button">
							{this.props.intl.formatMessage(i18n.viewComment)}
						</div>
					),
					onClick: () =>
						(this.props.pageMode === PageMode.EDITOR || this.props.pageMode === PageMode.LIVE) &&
						markerRef
							? this.commentFlagEditorLivePagesOnClick(
									markerRef,
									type,
									onClose,
									flagProps.commentType,
									this.props.push,
									commentIsAReply,
									url,
								)
							: this.commentFlagRendererOnClick(flagProps, type, onClose, url),
				},
			];

			// When the renderer annotation provider is enabled, if the comment thread is open and a new reply
			// is made in it, we don't need to show a flag, let the comments pubsub select the comment instead
			let showFlag = true;
			if (this.props.pageMode !== PageMode.RENDERER) {
				const { markerRef, commentType } = flagProps;

				if (commentType !== 'page' && markerRef) {
					const selectedAnnotationId = selectedAnnotations?.[0]?.id;
					showFlag = !selectedAnnotationId || selectedAnnotationId !== markerRef;
				}
			}

			if (showFlag) {
				const newFlag = await this.props.flagsStateContainer.showCustomFlag({
					...flagProps,
					actions: type === 'comment' ? commentActions : this.actions,
					onClose: () => {
						this.flagCleanup(type, flagProps.onClose);

						if (createAnalyticsEvent)
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									source: 'quickReload',
									action: 'closed',
									actionSubject: 'flag',
									actionSubjectId: 'flagCloseButton',
									attributes: {
										pageMode: this.props.pageMode,
										...(type === 'comment' && {
											commentType: flagProps.commentType,
										}),
										type: type === 'comment' ? 'comment' : 'edit', // flag type
									},
								},
							}).fire();
					},
					isAutoDismiss: true,
					...(type === 'comment'
						? {
								customIcon: (
									<AvatarGroup
										data={uniqueUsers.map((userInfo: UserInfo) => {
											return {
												name: userInfo.displayName,
												src: userInfo.profilePicturePath,
											};
										})}
										maxCount={2}
										appearance="stack"
										shouldPopupRenderToParent
									/>
								),
							}
						: {}),
				});

				// Send an analytics event when a flag appears, but only if there wasn't
				// one up already.
				if (
					this.showingFlagIdsForType.edit === undefined &&
					this.showingFlagIdsForType.comment === undefined
				) {
					if (createAnalyticsEvent)
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								source: 'quickReload',
								action: 'displayed',
								actionSubject: 'quickReloadFlag',
								attributes: {
									realtimeDataSource: this.getAnalyticsRealtimeDataSource(),
									pageMode: this.props.pageMode,
									...(type === 'comment' && {
										commentType: flagProps.commentType,
									}),
									type: type === 'comment' ? 'comment' : 'edit', // flag type
								},
							},
						}).fire();
				}

				this.showingFlagIdsForType[type] = newFlag.id;

				if (retiringFlagId !== undefined) {
					await this.props.flagsStateContainer.hideFlag(retiringFlagId);
				}

				if (this.props.isPageCommentsAutoReloadEnabled && flagProps.commentType === 'page') {
					this.setState((state) => {
						return {
							...state,
							mostRecentPageCommentFlagID: Number(newFlag.id),
						};
					});
				}
			}
		};

		if (this.props.setUserReadyCallback) {
			// set makeFlags as a callback to be executed when the user is ready for notifications
			this.props.setUserReadyCallback(func, type);
			// set hideAllFlags as a callback when the user is focused
			this.props.userFocusedCallback();
		} else {
			await func();
		}
	};

	hideFlags = () => {
		['edit', 'comment'].forEach(async (type) => {
			const flagId = this.showingFlagIdsForType[type];
			if (flagId !== undefined) {
				await this.props.flagsStateContainer.hideFlag(flagId);
				delete this.showingFlagIdsForType[flagId];
			}
		});
	};

	getAnalyticsRealtimeDataSource = () => {
		const { canUsePubSub } = this.props;
		if (canUsePubSub) return 'pubsubs';
		return 'polling';
	};

	render() {
		return null;
	}
}

export const QuickReloadFlagsI18nProvider = injectIntl(
	withAnalyticsEvents()(withRoutesContext(QuickReloadFlagsI18nProviderComponent)),
);
QuickReloadFlagsI18nProvider.displayName = 'QuickReloadFlagsI18nProvider';
